﻿import { CustomerReportType } from "../../customer/addEditCustomerDialog/AddEditCustomer.types";
import { LoginProvider, UserStartPage } from "../../user/User.types.ts";
import { ContractStandardField } from "../../entityTemplates/EntityTemplate.types.ts";
import { NavigationMenuItem } from "../../navigationMenuSettings/NavigationMenuSettings.types.ts";

export interface UserContextDto {
    startPage: UserStartPage;
    userPermissions: UserPermissions;
    showLegacyRiskAssessments: boolean;
    hiddenNavigationMenuItems: Array<NavigationMenuItem>;
}

export interface UserPermissions {
    canManageCustomers: boolean;
    canManageUsers: boolean;
    canManageGroupEntities: boolean;
    canManageMasterData: boolean;
    canCreateMasterData: boolean;
    canManageRiskAssessment: boolean;
    canChangeUserLoginProvider: boolean;
    annualWheelPermission: Permissions;
    processingActivityPermissions: Permissions;
    canManageRoles: boolean;
    canAccessAllData: boolean;
    canManageSystemStandardTemplates: boolean;
    canManageProcessingActivityStandardTemplates: boolean;
    canManageStandardTemplateData: boolean;
    canValidateProcessingActivities: boolean;
    processingActivityDataSubjectPermissions: ProcessingActivityDataSubjectPermissions;
    processingActivityMasterDataPermissions: ProcessingActivityMasterDataPermissions;
    processingActivityAssociationPermissions: ProcessingActivityAssociationPermissions;
    processingActivityDataCategoryPermissions: ProcessingActivityDataCategoryPermissions;
    processingActivityLegalBasesPermissions: ProcessingActivityLegalBasesPermissions;
    processingActivitySystemPermissions: ProcessingActivitySystemPermissions;
    processingActivityPolicyPermissions: ProcessingActivityPolicyPermissions;
    processingActivityDataProcessorsPermissions: ProcessingActivityDataProcessorsPermissions;
    processingActivityDataControllersPermissions: ProcessingActivityDataControllersPermissions;
    processingActivityDisclosuresPermissions: ProcessingActivityDisclosuresPermissions;
    processingActivitySourcesPermissions: ProcessingActivitySourcesPermissions;
    processingActivityJointDataControllerPermissions: ProcessingActivityJointDataControllerPermissions;
    canAccessRecord301: boolean;
    canAccessRecord302: boolean;
    riskPermissions: Permissions;
    riskTemplatePermissions: Permissions;
    taskPermissions: Permissions;
    customListPermissions: Permissions;
    systemPermissions: Permissions;
    companyPermissions: Permissions;
    companyDocumentationPermissions: Permissions;
    policyPermissions: Permissions;
    canManageProcessingActivtyComments: false;
    processingActivityStatusManagement?: ProcessingActivityStatusManagement;
    canManageProcessingActivityNote: false;
    canAccessAuditLog: false;
    canAccessProcessingActivityStatistic: boolean;
    canAccessTransfers: boolean;
    canDeleteDoocumentVersion: boolean;
    canSetDocumentStatus: boolean;
    riskVersionPermissions: Permissions;
    incidentLogPermissions: Permissions;
    processPermissions: Permissions;
    observationPermissions: Permissions;
    reportTypes: Array<CustomerReportType>;
    canManageAnnualWheelActivityTaskMasterData: boolean;
    canCopyProcessingActivity: boolean;
    canManageInternalAssessment: boolean;
    frameworkPermissions: Permissions;
    canManageAuditGroups: boolean;
    canAccessTags: boolean;
    contractManagementPermissions: ContractPermissions;
    canAccessProcessingActivityDocuments: boolean;
    canCreateLegalEntitiesInline: boolean;
    canDoFullDataExport: boolean;
    globalDocumentSearchAccess: boolean;
    canAccessAiFeature: boolean;
    canManageAiAdministration: boolean;
    canAccessEnterpriseRiskManagement: boolean;
    canManageApiKeys: boolean;
}

export interface Permissions {
    read: boolean;
    edit: boolean;
    create: boolean;
    delete: boolean;
}

export interface UserContext {
    language: string;
    customerName: string;
    customerCreated: Date;
    userProfileId: string;
    emailAddress: string;
    fullName: string;
    userInitials: string;
    permissions: UserPermissions;
    customerId: string;
    isFirstLogin: boolean;
    helpLink: string;
    gdpo: boolean;
    germanEnabled: boolean;
    created: Date;
    businessAreas: Array<string>;
    loginProvider: LoginProvider;
    startPage: UserStartPage;
    showLegacyRiskAssessments: boolean;
    hiddenNavigationMenuItems: Array<NavigationMenuItem>;
}

export interface ProcessingActivityDataSubjectPermissions {
    hasProcessorsPermission: boolean;
    hasDataSubjectsPermission: boolean;
    hasSecurityMeasurePermission: boolean;
    hasDataSourcePermission: boolean;
    hasDataSubjectCustomOnlyPermission: boolean;
    hasDataProcessorCustomOnlyPermission: boolean;
    permissions: Permissions;
}

export interface ProcessingActivityMasterDataPermissions {
    hasPurposePermission: boolean;
    permissions: Permissions;
}

export interface ProcessingActivityAssociationPermissions {
    hasGroupEntitiesPermission: boolean;
    hasOwnershipPermission: boolean;
    hasBusinessAreaPermission: boolean;
    hasAnySubPermissionAccess: boolean;
    permissions: Permissions;
}

export interface ProcessingActivityDataCategoryPermissions {
    permissions: Permissions;
}

export interface ProcessingActivityLegalBasesPermissions {
    hasLegalBasisPermission: boolean;
    hasDeletionPeriodPermission: boolean;
    permissions: Permissions;
}

export interface ProcessingActivitySystemPermissions {
    permissions: Permissions;
}

export interface ProcessingActivityPolicyPermissions {
    permissions: Permissions;
}

export interface ProcessingActivitySharingsPermissions {
    processingActivityDataProcessorsPermissions: ProcessingActivityDataProcessorsPermissions;
    processingActivityDataControllersPermissions: ProcessingActivityDataControllersPermissions;
    processingActivityDisclosuresPermissions: ProcessingActivityDisclosuresPermissions;
    processingActivitySourcesPermissions: ProcessingActivitySourcesPermissions;
}

export interface ProcessingActivityDataProcessorsPermissions {
    hasAgreementPermission: boolean;
    hasTiaAndTransferBasisPermission: boolean;
    hasResponsiblePermission: boolean;
    hasSubProcessorPermission: boolean;
    hasDataCategoriesPermission: boolean;
    hasNameAccess: boolean;
    permissions: Permissions;
}

export interface ProcessingActivityDataControllersPermissions {
    hasAgreementPermission: boolean;
    hasResponsiblePermission: boolean;
    hasSubProcessorPermission: boolean;
    hasTiaAndTransferBasisPermission: boolean;
    hasDataCategoriesPermission: boolean;
    hasNameAccess: boolean;
    permissions: Permissions;
}

export interface ProcessingActivityDisclosuresPermissions {
    hasAgreementPermission: boolean;
    hasTransferBasisPermission: boolean;
    hasBasisOfDisclosurePermission: boolean;
    hasResponsiblePermission: boolean;
    hasDataCategoriesPermission: boolean;
    hasNameAccess: boolean;
    permissions: Permissions;
}

export interface ProcessingActivitySourcesPermissions {
    hasAgreementPermission: boolean;
    hasResponsiblePermission: boolean;
    hasDataCategoriesPermission: boolean;
    hasNameAccess: boolean;
    permissions: Permissions;
}

export interface ProcessingActivityJointDataControllerPermissions {
    hasAgreementPermission: boolean;
    hasResponsiblePermission: boolean;
    hasDataCategoriesPermission: boolean;
    hasNameAccess: boolean;
    permissions: Permissions;
}

export interface ContractPermissions {
    permissions: Permissions;
    canAccessContractsOverview: boolean;
    canManageGlobalViews: boolean;
    canManageExcludedUsers: boolean;
    nonEditableFields: Array<ContractStandardField>;
    nonEditableCustomFieldIds: Array<string>;
}

export enum ProcessingActivityStatusManagement {
    Regular = 1,
    Draft = 2,
    Full = 3,
}
